import React from "react";
import {
  FaBirthdayCake,
  FaDumbbell,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFlag,
  FaIdCard,
  FaLink,
  FaMapMarkerAlt,
  FaPen,
  FaTrash,
  FaUser,
  FaVenusMars,
} from "react-icons/fa";

import { departementsEtRegions } from "../../../../utils/departements";

import EmergencyContactCard from "./EmergencyContactCard";
import ProfileInfoCard from "./ProfileInfoCard";
import QuickWeightCard from "./QuickWeightCard";

import {
  formatDate,
  getPhotoUrl,
  isValidHttpUrl,
} from "../utils/judokaProfileUtils";

const getNomDepartement = (code) => {
  if (!code) return "Non spécifié";

  const departement =
    departementsEtRegions.find(
      (dep) =>
        String(dep.code) === String(code),
    );

  return departement
    ? departement.departement
    : "Inconnu";
};

function JudokaInfoGrid({
  judoka,
  quickWeight,
  weightSaving,
  onEdit,
  onDelete,
  onWeightChange,
  onWeightAdjust,
  onWeightSave,
  t,
}) {
  const photo = getPhotoUrl(judoka);
  const hasOnlinePhoto =
    isValidHttpUrl(photo);

  return (
    <div className="space-y-6">
      <EmergencyContactCard
        judoka={judoka}
      />

      <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-200">
              Informations
            </p>

            <h2 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
              Identité sportive
            </h2>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-white hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
            >
              <FaPen className="text-xs" />
              Modifier
            </button>

            <button
              type="button"
              onClick={onDelete}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 transition hover:bg-red-100 hover:shadow-md dark:border-red-400/20 dark:bg-red-500/10 dark:text-red-200 dark:hover:bg-red-500/15"
            >
              <FaTrash className="text-xs" />
              Supprimer
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <ProfileInfoCard
            icon={FaUser}
            label={t(
              "judokaProfile.firstName",
              {
                defaultValue: "Prénom",
              },
            )}
            value={judoka.prenom}
          />

          <ProfileInfoCard
            icon={FaUser}
            label={t(
              "judokaProfile.lastName",
              {
                defaultValue: "Nom",
              },
            )}
            value={judoka.nom}
          />

          <ProfileInfoCard
            icon={FaVenusMars}
            label={t(
              "judokaProfile.gender",
              {
                defaultValue: "Genre",
              },
            )}
            value={judoka.genre}
          />

          <ProfileInfoCard
            icon={FaBirthdayCake}
            label={t(
              "judokaProfile.birthDate",
              {
                defaultValue:
                  "Date de naissance",
              },
            )}
            value={formatDate(
              judoka.date_naissance,
            )}
          />

          <QuickWeightCard
            value={quickWeight}
            onChange={onWeightChange}
            onAdjust={onWeightAdjust}
            onSave={onWeightSave}
            saving={weightSaving}
            t={t}
          />

          <ProfileInfoCard
            icon={FaIdCard}
            label={t(
              "judokaProfile.license",
              {
                defaultValue: "Licence",
              },
            )}
            value={judoka.licence_number}
          />

          <div className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.05]">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                <FaFileAlt />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Fichier licence
                </p>

                {judoka.licence_url ? (
                  <a
                    href={
                      judoka.licence_url
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-2 break-words text-sm font-bold leading-6 text-sky-700 transition hover:text-orange-600 dark:text-sky-200"
                  >
                    Voir la licence

                    <FaExternalLinkAlt className="text-[10px]" />
                  </a>
                ) : (
                  <p className="mt-1 text-sm font-bold leading-6 text-slate-800 dark:text-white">
                    Non renseigné
                  </p>
                )}
              </div>
            </div>
          </div>

          <ProfileInfoCard
            icon={FaDumbbell}
            label={t(
              "judokaProfile.club",
              {
                defaultValue: "Club",
              },
            )}
            value={judoka.club}
          />

          <ProfileInfoCard
            icon={FaFlag}
            label={t(
              "judokaProfile.country",
              {
                defaultValue: "Pays",
              },
            )}
            value={judoka.pays}
          />

          <div className="sm:col-span-2">
            <ProfileInfoCard
              icon={FaMapMarkerAlt}
              label={t(
                "judokaProfile.region",
                {
                  defaultValue:
                    "Département / province",
                },
              )}
              value={getNomDepartement(
                judoka.departement,
              )}
            />
          </div>
        </div>
      </div>

      <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
            <FaLink />
          </div>

          <div>
            <h2 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
              Photo du judoka
            </h2>

            <p className="mt-2 text-sm font-medium leading-7 text-slate-600 dark:text-white/55">
              Vous pouvez choisir un avatar
              intégré ou utiliser l’URL d’une
              photo hébergée en ligne. Cette
              image sera utilisée sur les pages
              de progression du judoka.
            </p>

            {hasOnlinePhoto && (
              <a
                href={photo}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-sky-700 transition hover:text-orange-600 dark:text-sky-200"
              >
                Ouvrir la photo actuelle

                <FaExternalLinkAlt className="text-[10px]" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default JudokaInfoGrid;